import { useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import API_URL from '../constants/environment';
import { useContext, useState } from 'react';
import SessionContext from '../contexts/SessionContext';

export interface PullPartsTraderLocationsInput {
	organizationId: string;
	username: string;
	password: string;
}

export interface PartsTraderLocation {
	id: string;
	name: string;
}

interface SubmitResponse {
	organizationId: string;
}

interface PullStatusResponse {
	organizationId: string;
	target: string;
	status: 'pending' | 'in-progress' | 'completed' | 'failed' | null;
	locations: PartsTraderLocation[] | null;
	locationsReady?: boolean;
	error: string | null;
}

export const usePullPartsTraderLocations = () => {
	const sessionContext = useContext(SessionContext);
	const [organizationId, setOrganizationId] = useState<string | null>(null);

	const submit = useMutation({
		mutationFn: async (input: PullPartsTraderLocationsInput) => {
			const { data } = await axios.post<SubmitResponse>(
				`${API_URL}/onboarding/pull-parts-trader-locations`,
				input,
				{ headers: { Authorization: sessionContext?.sessionId ?? '' } }
			);
			setOrganizationId(data.organizationId);
			return data;
		},
	});

	const status = useQuery({
		queryKey: ['pull-pt-status', organizationId],
		queryFn: async () => {
			const { data } = await axios.get<PullStatusResponse>(
				`${API_URL}/onboarding/pull-status`,
				{ params: { organizationId, target: 'partstrader' }, headers: { Authorization: sessionContext?.sessionId ?? '' } }
			);
			return data;
		},
		enabled: !!organizationId,
		refetchInterval: (query) => {
			const data = query.state.data;
			if (data?.status === 'completed' || data?.status === 'failed') {
				return false;
			}
			return 3000;
		},
	});

	const reset = () => {
		setOrganizationId(null);
	};

	return {
		submit,
		status,
		organizationId,
		reset,
		isPolling: !!organizationId && status.data?.status !== 'completed' && status.data?.status !== 'failed',
		locations: status.data?.locations ?? null,
		locationsReady: status.data?.locationsReady === true,
		error: status.data?.error ?? null,
		isComplete: status.data?.status === 'completed',
		isFailed: status.data?.status === 'failed',
	};
};
